/**
 * GraphQL Subscriptions for Real-time Updates
 * 
 * WebSocket-based subscriptions for live workflow event streaming
 * Following append-only architecture with real-time notifications
 */

import { gql, DocumentNode } from '@apollo/client';

// Instance State Change Subscriptions
export const INSTANCE_STATE_CHANGED = gql`
  subscription InstanceStateChanged($tenant: String!, $instanceId: String) {
    instanceStateChanged(tenant: $tenant, instanceId: $instanceId) {
      instanceId
      previousState
      currentState
      status
      timestamp
      triggeredBy
      context
      metadata
    }
  }
`;

export const WORKFLOW_INSTANCES_UPDATED = gql`
  subscription WorkflowInstancesUpdated($tenant: String!, $workflowDefinitionKey: String) {
    workflowInstancesUpdated(tenant: $tenant, workflowDefinitionKey: $workflowDefinitionKey) {
      instanceId
      workflowDefinitionKey
      status
      currentState
      createdAt
      updatedAt
      completedAt
      context
    }
  }
`;

// Task Assignment and Status Subscriptions
export const TASK_ASSIGNED = gql`
  subscription TaskAssigned($tenant: String!, $assignee: String) {
    taskAssigned(tenant: $tenant, assignee: $assignee) {
      taskId
      title
      assignee
      assigneeGroup
      priority
      dueDate
      workflowInstanceId
      taskType
      status
      createdAt
      assignedAt
      metadata
    }
  }
`;

export const TASK_STATUS_UPDATED = gql`
  subscription TaskStatusUpdated($tenant: String!, $taskId: String) {
    taskStatusUpdated(tenant: $tenant, taskId: $taskId) {
      taskId
      previousStatus
      currentStatus
      updatedBy
      updatedAt
      comments
      approvalDecision
      metadata
    }
  }
`;

export const TASKS_FOR_USER_UPDATED = gql`
  subscription TasksForUserUpdated($tenant: String!, $userId: String!) {
    tasksForUserUpdated(tenant: $tenant, userId: $userId) {
      taskId
      title
      priority
      status
      dueDate
      workflowInstanceId
      assignedAt
      updatedAt
    }
  }
`;

// Task Collaboration Subscription (Phase 3 - Real-time multi-user editing)
export const TASK_COLLABORATION_EVENTS = gql`
  subscription TaskCollaborationEvents($taskId: String!) {
    taskCollaborationEvents(taskId: $taskId) {
      taskId
      tenant
      eventType
      userId
      userName
      fieldName
      timestamp
      metadata
    }
  }
`;

// Workflow Execution Subscriptions
export const WORKFLOW_EXECUTION_EVENT = gql`
  subscription WorkflowExecutionEvent($tenant: String!, $instanceId: String) {
    workflowExecutionEvent(tenant: $tenant, instanceId: $instanceId) {
      instanceId
      eventType
      eventData
      timestamp
      stateName
      transitionName
      executionResult
      errorDetails
      duration
      metadata
    }
  }
`;

export const SIGNAL_RECEIVED = gql`
  subscription SignalReceived($tenant: String!, $instanceId: String) {
    signalReceived(tenant: $tenant, instanceId: $instanceId) {
      instanceId
      signalName
      payload
      correlationId
      timestamp
      processedAt
      result
      metadata
    }
  }
`;

// Schedule Execution Subscriptions
export const SCHEDULE_EXECUTED = gql`
  subscription ScheduleExecuted($tenant: String!, $scheduleId: String) {
    scheduleExecuted(tenant: $tenant, scheduleId: $scheduleId) {
      scheduleId
      scheduleName
      executionId
      executionTime
      expectedTime
      status
      triggeredWorkflows
      duration
      errorDetails
      nextExecutionTime
      metadata
    }
  }
`;

export const SCHEDULE_STATUS_CHANGED = gql`
  subscription ScheduleStatusChanged($tenant: String!) {
    scheduleStatusChanged(tenant: $tenant) {
      scheduleId
      scheduleName
      previousStatus
      currentStatus
      changedBy
      changedAt
      reason
      nextExecutionTime
      metadata
    }
  }
`;

// Policy Evaluation Subscriptions
export const POLICY_EVALUATION_EVENT = gql`
  subscription PolicyEvaluationEvent($tenant: String!, $policyId: String) {
    policyEvaluationEvent(tenant: $tenant, policyId: $policyId) {
      policyId
      policyName
      evaluationId
      instanceId
      evaluationResult
      executionTime
      inputData
      outputData
      errorDetails
      enforcementMode
      timestamp
      metadata
    }
  }
`;

export const POLICY_VIOLATION_DETECTED = gql`
  subscription PolicyViolationDetected($tenant: String!) {
    policyViolationDetected(tenant: $tenant) {
      violationId
      policyId
      policyName
      instanceId
      violationType
      severity
      description
      context
      detectedAt
      resolvedAt
      resolutionAction
      metadata
    }
  }
`;

// Resource and Integration Subscriptions
export const RESOURCE_STATUS_CHANGED = gql`
  subscription ResourceStatusChanged($tenant: String!, $resourceId: String) {
    resourceStatusChanged(tenant: $tenant, resourceId: $resourceId) {
      resourceId
      resourceName
      previousStatus
      currentStatus
      healthCheck
      availability
      responseTime
      errorCount
      lastChecked
      metadata
    }
  }
`;

export const INTEGRATION_EVENT = gql`
  subscription IntegrationEvent($tenant: String!, $integrationId: String) {
    integrationEvent(tenant: $tenant, integrationId: $integrationId) {
      integrationId
      integrationName
      eventType
      eventData
      instanceId
      timestamp
      status
      responseTime
      retryCount
      errorDetails
      metadata
    }
  }
`;

// Escalation Subscriptions
export const ESCALATION_TRIGGERED = gql`
  subscription EscalationTriggered($tenant: String!) {
    escalationTriggered(tenant: $tenant) {
      escalationId
      escalationName
      instanceId
      triggeredBy
      triggerReason
      escalationLevel
      timestamp
      assignedTo
      actions
      deadline
      metadata
    }
  }
`;

export const ESCALATION_RESOLVED = gql`
  subscription EscalationResolved($tenant: String!, $escalationId: String) {
    escalationResolved(tenant: $tenant, escalationId: $escalationId) {
      escalationId
      instanceId
      resolvedBy
      resolutionAction
      resolvedAt
      outcome
      comments
      metadata
    }
  }
`;

// System Health and Performance Subscriptions
export const SYSTEM_HEALTH_UPDATE = gql`
  subscription SystemHealthUpdate($tenant: String!) {
    systemHealthUpdate(tenant: $tenant) {
      timestamp
      overallHealth
      services
      metrics {
        activeWorkflows
        completedWorkflows
        failedWorkflows
        queuedTasks
        systemLoad
        memoryUsage
        responseTime
      }
      alerts {
        level
        message
        component
        timestamp
      }
    }
  }
`;

export const PERFORMANCE_METRICS_UPDATE = gql`
  subscription PerformanceMetricsUpdate($tenant: String!) {
    performanceMetricsUpdate(tenant: $tenant) {
      timestamp
      metrics {
        throughput
        latency
        errorRate
        activeConnections
        queueDepth
        resourceUtilization
      }
      trends {
        hourly
        daily
        weekly
      }
    }
  }
`;

// Job Status and Execution Subscriptions
export const JOB_STATUS_CHANGED = gql`
  subscription JobStatusChanged($tenant: String!, $jobId: String) {
    jobStatusChanged(tenant: $tenant, jobId: $jobId) {
      jobId
      jobType
      previousStatus
      currentStatus
      progress
      startedAt
      completedAt
      duration
      result
      errorDetails
      metadata
    }
  }
`;

export const BATCH_JOB_COMPLETED = gql`
  subscription BatchJobCompleted($tenant: String!) {
    batchJobCompleted(tenant: $tenant) {
      batchId
      jobType
      totalJobs
      completedJobs
      failedJobs
      successRate
      duration
      results
      metadata
    }
  }
`;

// Context and State Management Subscriptions
export const CONTEXT_UPDATED = gql`
  subscription ContextUpdated($tenant: String!, $contextId: String) {
    contextUpdated(tenant: $tenant, contextId: $contextId) {
      contextId
      instanceId
      previousData
      currentData
      updatedBy
      updatedAt
      changeType
      metadata
    }
  }
`;

export const FEATURE_FLAG_CHANGED = gql`
  subscription FeatureFlagChanged($tenant: String!) {
    featureFlagChanged(tenant: $tenant) {
      flagName
      previousValue
      currentValue
      changedBy
      changedAt
      affectedInstances
      rolloutPercentage
      metadata
    }
  }
`;

// Timeline and Audit Subscriptions
export const TIMELINE_EVENT_ADDED = gql`
  subscription TimelineEventAdded($tenant: String!, $instanceId: String) {
    timelineEventAdded(tenant: $tenant, instanceId: $instanceId) {
      eventId
      instanceId
      eventType
      eventData
      timestamp
      actor
      description
      metadata
    }
  }
`;

export const AUDIT_EVENT = gql`
  subscription AuditEvent($tenant: String!) {
    auditEvent(tenant: $tenant) {
      eventId
      eventType
      entityType
      entityId
      action
      actor
      timestamp
      beforeData
      afterData
      metadata
    }
  }
`;

// Actor and Permission Subscriptions
export const ACTOR_STATUS_CHANGED = gql`
  subscription ActorStatusChanged($tenant: String!, $actorId: String) {
    actorStatusChanged(tenant: $tenant, actorId: $actorId) {
      actorId
      actorName
      previousStatus
      currentStatus
      capabilities
      workload
      lastActivity
      metadata
    }
  }
`;

export const ROLE_ASSIGNMENT_CHANGED = gql`
  subscription RoleAssignmentChanged($tenant: String!, $actorId: String) {
    roleAssignmentChanged(tenant: $tenant, actorId: $actorId) {
      actorId
      roleId
      roleName
      action
      assignedBy
      assignedAt
      expiresAt
      permissions
      metadata
    }
  }
`;

// Error and Exception Subscriptions
export const ERROR_OCCURRED = gql`
  subscription ErrorOccurred($tenant: String!) {
    errorOccurred(tenant: $tenant) {
      errorId
      errorType
      severity
      message
      stackTrace
      instanceId
      component
      timestamp
      context
      correlationId
      metadata
    }
  }
`;

export const EXCEPTION_HANDLED = gql`
  subscription ExceptionHandled($tenant: String!) {
    exceptionHandled(tenant: $tenant) {
      exceptionId
      instanceId
      exceptionType
      handlerType
      resolution
      handledBy
      handledAt
      retryAttempts
      finalOutcome
      metadata
    }
  }
`;

// Comprehensive Multi-Event Subscriptions
export const WORKFLOW_ACTIVITY_STREAM = gql`
  subscription WorkflowActivityStream($tenant: String!, $instanceIds: [String!]) {
    workflowActivityStream(tenant: $tenant, instanceIds: $instanceIds) {
      eventId
      eventType
      instanceId
      timestamp
      eventData
      metadata
    }
  }
`;

export const TENANT_ACTIVITY_STREAM = gql`
  subscription TenantActivityStream($tenant: String!, $eventTypes: [String!]) {
    tenantActivityStream(tenant: $tenant, eventTypes: $eventTypes) {
      eventId
      eventType
      entityType
      entityId
      timestamp
      eventData
      priority
      metadata
    }
  }
`;

// Input Types for Subscriptions
export interface InstanceStateChangedInput {
  tenant: string;
  instanceId?: string;
}

export interface TaskAssignedInput {
  tenant: string;
  assignee?: string;
}

export interface TaskStatusUpdatedInput {
  tenant: string;
  taskId?: string;
}

export interface WorkflowExecutionEventInput {
  tenant: string;
  instanceId?: string;
}

export interface ScheduleExecutedInput {
  tenant: string;
  scheduleId?: string;
}

export interface PolicyEvaluationEventInput {
  tenant: string;
  policyId?: string;
}

export interface ResourceStatusChangedInput {
  tenant: string;
  resourceId?: string;
}

export interface IntegrationEventInput {
  tenant: string;
  integrationId?: string;
}

export interface WorkflowActivityStreamInput {
  tenant: string;
  instanceIds?: string[];
}

export interface TenantActivityStreamInput {
  tenant: string;
  eventTypes?: string[];
}

export interface TaskCollaborationEventsInput {
  taskId: string;
}

// Phase 3: Task Collaboration Event Type
export interface TaskCollaborationEvent {
  taskId: string;
  tenant: string;
  eventType: 'user_joined' | 'user_left' | 'field_locked' | 'field_unlocked' | 'field_edited' | 'conflict_detected';
  userId: string;
  userName: string;
  fieldName?: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

// Subscription Event Types
export type SubscriptionEventType = 
  | 'INSTANCE_STATE_CHANGED'
  | 'TASK_ASSIGNED'
  | 'TASK_STATUS_UPDATED'
  | 'WORKFLOW_EXECUTION_EVENT'
  | 'SIGNAL_RECEIVED'
  | 'SCHEDULE_EXECUTED'
  | 'SCHEDULE_STATUS_CHANGED'
  | 'POLICY_EVALUATION_EVENT'
  | 'POLICY_VIOLATION_DETECTED'
  | 'RESOURCE_STATUS_CHANGED'
  | 'INTEGRATION_EVENT'
  | 'ESCALATION_TRIGGERED'
  | 'ESCALATION_RESOLVED'
  | 'SYSTEM_HEALTH_UPDATE'
  | 'PERFORMANCE_METRICS_UPDATE'
  | 'JOB_STATUS_CHANGED'
  | 'BATCH_JOB_COMPLETED'
  | 'CONTEXT_UPDATED'
  | 'FEATURE_FLAG_CHANGED'
  | 'TIMELINE_EVENT_ADDED'
  | 'AUDIT_EVENT'
  | 'ACTOR_STATUS_CHANGED'
  | 'ROLE_ASSIGNMENT_CHANGED'
  | 'ERROR_OCCURRED'
  | 'EXCEPTION_HANDLED';

// Event Priority Levels
export enum EventPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

// Event Categories for filtering
export enum EventCategory {
  WORKFLOW = 'WORKFLOW',
  TASK = 'TASK',
  SCHEDULE = 'SCHEDULE',
  POLICY = 'POLICY',
  RESOURCE = 'RESOURCE',
  INTEGRATION = 'INTEGRATION',
  ESCALATION = 'ESCALATION',
  SYSTEM = 'SYSTEM',
  AUDIT = 'AUDIT',
  ERROR = 'ERROR'
}

// Subscription Management Helper
export interface SubscriptionConfig {
  subscription: DocumentNode;
  variables: Record<string, unknown>;
  onData?: (data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

// Export all subscriptions for easy import
export const WorkflowSubscriptions = {
  // Instance subscriptions
  INSTANCE_STATE_CHANGED,
  WORKFLOW_INSTANCES_UPDATED,
  
  // Task subscriptions
  TASK_ASSIGNED,
  TASK_STATUS_UPDATED,
  TASKS_FOR_USER_UPDATED,
  
  // Execution subscriptions
  WORKFLOW_EXECUTION_EVENT,
  SIGNAL_RECEIVED,
  
  // Schedule subscriptions
  SCHEDULE_EXECUTED,
  SCHEDULE_STATUS_CHANGED,
  
  // Policy subscriptions
  POLICY_EVALUATION_EVENT,
  POLICY_VIOLATION_DETECTED,
  
  // Resource subscriptions
  RESOURCE_STATUS_CHANGED,
  INTEGRATION_EVENT,
  
  // Escalation subscriptions
  ESCALATION_TRIGGERED,
  ESCALATION_RESOLVED,
  
  // System subscriptions
  SYSTEM_HEALTH_UPDATE,
  PERFORMANCE_METRICS_UPDATE,
  
  // Job subscriptions
  JOB_STATUS_CHANGED,
  BATCH_JOB_COMPLETED,
  
  // Context subscriptions
  CONTEXT_UPDATED,
  FEATURE_FLAG_CHANGED,
  
  // Timeline subscriptions
  TIMELINE_EVENT_ADDED,
  AUDIT_EVENT,
  
  // Actor subscriptions
  ACTOR_STATUS_CHANGED,
  ROLE_ASSIGNMENT_CHANGED,
  
  // Error subscriptions
  ERROR_OCCURRED,
  EXCEPTION_HANDLED,
  
  // Activity streams
  WORKFLOW_ACTIVITY_STREAM,
  TENANT_ACTIVITY_STREAM
} as const;